import { useRef, useEffect, useCallback } from "react";
import connection from "@shared/utils/connection";
import validateResponse from "./validateResponse";
import API from "@shared/config/services/url.config";

const useConnection = ({ debounce = false, delay = 300, enableAbort = true, label, typeRequest } = {}) => {
  const controllerRef = useRef(null);
  const debounceRef = useRef({ t: null, rejectPrev: null });

  useEffect(() => {
    return () => {
      if (controllerRef.current) controllerRef.current.abort();

      if (debounceRef.current.t) clearTimeout(debounceRef.current.t);
      if (debounceRef.current.rejectPrev) debounceRef.current.rejectPrev(new Error("Debounced"));
    };
  }, []);

  const makeRequest = useCallback(
    (options) => {
      const labelMensagge = options.label || label || null;
      const typeMakeRequest = options.typeRequest || typeRequest || null;

      const api = ("api" in options && options.api) ? API[options.api] : (API.E2E || API.DEFAULT || "");
      const url = `${api}${options.url}`;

      const run = async () => {
        if (controllerRef.current) controllerRef.current.abort();

        let controller;
        if (enableAbort) {
          controller = new AbortController();
          controllerRef.current = controller;
        }

        const responseConnection = await connection({
          ...options,
          url,
          ...(enableAbort ? { signal: controller.signal } : {}),
        });

        return labelMensagge
          ? validateResponse(responseConnection, labelMensagge, typeMakeRequest, options)
          : responseConnection;
      };

      if (!debounce) return run();

      if (debounceRef.current.t) clearTimeout(debounceRef.current.t);
      if (debounceRef.current.rejectPrev) debounceRef.current.rejectPrev(new Error("Debounced"));

      return new Promise((resolve, reject) => {
        debounceRef.current.rejectPrev = reject;
        debounceRef.current.t = setTimeout(async () => {
          debounceRef.current.t = null;
          debounceRef.current.rejectPrev = null;
          try {
            resolve(await run());
          } catch (e) {
            reject(e);
          }
        }, delay);
      });
    },
    [debounce, delay, enableAbort, label, typeRequest]
  );

  return makeRequest;
};

export default useConnection;
